/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form, FormGroup, Label } from 'reactstrap';
import { duplicateSurvey, loadSurveyListSummaryByInitiativeId, reloadSurveyListSummary } from '../../../../../actions/survey';
import { ROUTES } from '../../../../../constants/routes';
import { AnalyticsEvents } from '../../../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../../../services/analytics/AnalyticsService';
import { reloadSurveyList } from '../../../../../slice/initiativeSurveyListSlice';
import { getMonthNonJSOptions, getYearDropdownOptions } from '../../../../../utils/date';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { CheckboxInput, ConfigFormData, CheckboxProps, updateFn } from '@g17eco/molecules/form';
import { Loader } from '@g17eco/atoms/loader';
import { generateUrl } from '../../../../../routes/util';
import { QUESTION, SURVEY } from '@constants/terminology';
import { YEARS_BACK, YEARS_NEXT } from '../../../../../constants/date';
import { DefaultBlueprintCode } from '@features/survey-configuration/types';
import { useAppDispatch, useAppSelector } from '../../../../../reducers';
import { hasSurveyConflict } from '@utils/survey';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SurveyType } from '@g17eco/types/surveyCommon';

interface SurveyDuplicateFormProps {
  survey: any;
  toggle: () => void;
}

const SurveyDuplicateForm = ({ survey, toggle }: SurveyDuplicateFormProps) => {
  const { initiativeId, type } = survey;
  const isCombinedReport = type === SurveyType.Aggregation;
  const [form, setForm] = useState<ConfigFormData>({
    year: '',
    month: '',
    delegation: isCombinedReport,
    settings: isCombinedReport,
    scope: isCombinedReport,
    hasData: isCombinedReport,
    metricStatus: isCombinedReport,
  });

  const [saving, setSaving] = useState(false);
  const [errorMessage, setMessage] = useState('');
  const dispatch = useAppDispatch();
  const history = useHistory();

  const surveyListSummaryState = useAppSelector((state) => state.surveyListSummary);

  const monthOptions = getMonthNonJSOptions();
  const yearOptions = getYearDropdownOptions(YEARS_BACK, YEARS_NEXT);
  const selectedMonth = monthOptions.find((o) => o.value === form.month);
  const selectedYear = yearOptions.find((o) => o.value === form.year);

  useEffect(() => {
    if (initiativeId && !isCombinedReport) {
      dispatch(loadSurveyListSummaryByInitiativeId(initiativeId));
    }
  }, [dispatch, initiativeId, isCombinedReport]);

  const hasConflict = !isCombinedReport && hasSurveyConflict(
    surveyListSummaryState.data,
    {
      initiativeId,
      year: Number(form.year),
      month: Number(form.month) - 1, // Back to start from 0
      type: survey.type,
      sourceName: survey.sourceName,
      period: survey.period,
    },
    undefined
  );

  const isOutdatedBlueprintVersion = survey.sourceName !== DefaultBlueprintCode;

  const updateForm: updateFn = (update) => {
    if (update.code === 'scope' && update.value === false) {
      setForm({ ...form, delegation: false, hasData: false, [update.code]: update.value })
      return
    }
    setForm({
      ...form,
      [update.code]: update.value
    })
  }

  const checkOptions: CheckboxProps = {
    groupCode: 'Report Options',
    form: {},
    options: [
      {
        code: 'settings',
        name: 'Settings',
        instructions: `Copy over ${SURVEY.SINGULAR} settings such as 'Verification Required' or 'Evidence Required'.`,
        disabled: isCombinedReport,
      },
      {
        code: 'scope',
        name: 'Scope',
        instructions: `Re-apply the same SDGs, standards, frameworks or Custom ${
          QUESTION.CAPITALIZED_PLURAL
        } to scope. ${
          isOutdatedBlueprintVersion
            ? `Please note, we keep the ${QUESTION.PLURAL} in global standards up to date. Some ${QUESTION.PLURAL} might slightly change if they have been updated by the global standards body (such as GRI) since your last ${SURVEY.SINGULAR}.`
            : ''
        }`,
        disabled: isCombinedReport,
      },
      {
        instructions: `Re-assign ${SURVEY.SINGULAR} and ${QUESTION.SINGULAR} delegation to the same administrators, contributors, verifiers and monitors.`,
        code: 'delegation',
        name: 'Delegation',
        disabled: isCombinedReport || !form.scope,
      },
      {
        instructions: `Make a copy of the latest data entries for the ${QUESTION.PLURAL}. All data will be re-assigned to be owned by you, and will not be auto-verified.`,
        code: 'hasData',
        name: 'Data',
        disabled: isCombinedReport || !form.scope,
      },
      ...(isCombinedReport
        ? [
            {
              instructions: `Copy over ${SURVEY.SINGULAR} metric status settings and re-apply the same metric status for the ${QUESTION.PLURAL}`,
              code: 'metricStatus',
              name: 'Metric status',
              disabled: isCombinedReport || !form.scope,
            },
          ]
        : []),
    ],
    onChange: () => {},
  };

  const requiredFields: (keyof ConfigFormData)[] = [
    'year',
    'month',
  ];


  const isConfirmBtnDisabled = (form: ConfigFormData) => {
    if (hasConflict) {
      return true;
    }

    return requiredFields.some((f) => undefined === form[f] || form[f] === '');
  };

  const isConfirmDisabled = saving || isConfirmBtnDisabled(form) || !!errorMessage;

  const handleClick = async () => {
    setSaving(true)
    try {
      const createdSurvey = await duplicateSurvey(survey._id, form)
      setSaving(false)
      const analytics = getAnalytics();
      await analytics.track(AnalyticsEvents.SurveyDuplicate, {
        surveyId: createdSurvey._id,
        initiativeId: createdSurvey.initiativeId
      });
      toggle();
      dispatch(reloadSurveyList())
      dispatch(reloadSurveyListSummary());
      if (createdSurvey._id) {
        const page = isCombinedReport ? 'update-combined' : 'scope';
        history.push(
          generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId: createdSurvey.initiativeId, surveyId: createdSurvey._id, page })
        );
      }
    } catch (e) {
      setSaving(false)
      setMessage(e.message)
    }
  }

  if (!surveyListSummaryState.loaded) {
    return <Loader />;
  }

  return <>
    <BasicAlert type={'danger'}>{errorMessage}</BasicAlert>
    {saving && <Loader />}
    <Form className='text-ThemeTextDark'>
      <FormGroup>
        <div className='row'>
          <div className='col'>
            <Label className='d-block form-check-label subtitle-container strong' for={'reporting-company'}>
              Reporting Period
            </Label>
              {isCombinedReport ? null : (
                <Label className='d-block subtitle-container' for={'reporting-company'}>
                  Please select the reporting period for the new report.
                </Label>
              )}
          </div>
        </div>
        <div className='row mt-2'>
          <BasicAlert hide={!hasConflict} type={'warning'} >
            <div>
              <b>WARNING:</b> A Report already exists for the selected Period. Please update your settings.
            </div>
          </BasicAlert>
          <div className='col-12 col-md-6'>
            <SelectFactory
              selectType={SelectTypes.SingleSelect}
              placeholder={'Select Month'}
              className='w-100'
              onChange={(option) => updateForm({ code: 'month', value: option?.value })}
              value={selectedMonth}
              isSearchable={true}
              options={monthOptions}
            />
          </div>
          <div className='col-12 col-md-6'>
            <SelectFactory<number>
              selectType={SelectTypes.SingleSelect}
              placeholder={'Select Year'}
              className='w-100'
              onChange={(option) => updateForm({ code: 'year', value: option?.value })}
              value={selectedYear}
              isSearchable={true}
              options={yearOptions}
            />
          </div>
        </div>
      </FormGroup>
      <FormGroup>
        <div className='row mt-3'>
          <div className='col'>
            <Label className='d-block form-check-label subtitle-container strong' for={'reporting-company'}>
              {SURVEY.CAPITALIZED_SINGULAR} Elements to Duplicate
            </Label>
            <Label className='d-block subtitle-container' for={'reporting-company'}>
              Please select which elements to copy from the current {SURVEY.SINGULAR}.
            </Label>
          </div>
        </div>
        <div className='row mt-2'>
          <div className='col ml-2'>
            <CheckboxInput groupCode={checkOptions.groupCode} options={checkOptions.options} form={form} onChange={updateForm} />
          </div>
        </div>
      </FormGroup>
      <div className='text-right text-nowrap'>
        <Button color='link-secondary' onClick={toggle}>Cancel</Button>
        <SubmitButton disabled={isConfirmDisabled} className='ml-2 btn-secondary' onClick={handleClick}>Create duplicate</SubmitButton>
      </div>
    </Form>
  </>
}

export default SurveyDuplicateForm;
